/**
 * AP-32 Nachtrag 6 — **Verrechnung des Lieferscheins.**
 *
 * Ablauf: Lieferschein erstellt → Kunde bestätigt → Sachbearbeiter kennzeichnet
 * „zur Verrechnung freigegeben" (optional mit dem unterschriebenen PDF) → die
 * Buchhaltung wandelt die freigegebenen Lieferscheine in Rechnungen um. Der
 * Verrechnungsstand kommt aus den Rechnungen; für Sonderfälle lässt er sich
 * händisch setzen.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Paperclip, Upload } from 'lucide-react'
import { DELIVERY_BILLING_STATUSES } from '@symphenia/shared-types'
import { downloadFile } from '@/lib/download'
import { fehlertext } from '@/lib/fehlertext'
import { documentsApi } from '../api/documents.api'
import type { BillingStatus, DocumentDetail } from '../types/document.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60'
const label = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground'

interface Props {
  beleg: DocumentDetail
  onChange: (b: DocumentDetail) => void
}

export function DeliveryBillingBlock({ beleg, onChange }: Props) {
  const { t } = useTranslation()
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const v = beleg.billing
  if (!v) return null
  const darf = beleg.rights.billing

  const aktion = async (schritt: () => Promise<{ data: { data: DocumentDetail } }>) => {
    setFehler(null)
    setLaeuft(true)
    try {
      onChange((await schritt()).data.data)
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  const laden = async () => {
    setFehler(null)
    try {
      const { data } = await documentsApi.signedPdf(beleg.id)
      downloadFile(data, v.signedPdfName ?? 'lieferschein-unterschrieben.pdf', 'application/pdf')
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.billing.title')}</h2>
      {fehler && <div className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <p className={label}>{t('documents.billing.status')}</p>
          <p className="mb-2 text-sm font-medium">
            {t(`documents.billing.statuses.${v.status}`)}
            {v.manual ? ` (${t('documents.billing.manualShort')})` : ''}
          </p>
          <select
            disabled={!darf || laeuft}
            value={v.manual ?? ''}
            onChange={(e) => void aktion(() => documentsApi.setBilling(beleg.id, { manualStatus: (e.target.value || null) as BillingStatus | null }))}
            className={eingabe}
          >
            <option value="">{t('documents.billing.auto', { status: t(`documents.billing.statuses.${v.computed}`) })}</option>
            {DELIVERY_BILLING_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`documents.billing.statuses.${s}`)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">{t('documents.billing.manualHint')}</p>
        </div>

        <div>
          <p className={label}>{t('documents.billing.release')}</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={!darf || laeuft}
              checked={!!v.releasedAt}
              onChange={(e) => void aktion(() => documentsApi.setBilling(beleg.id, { released: e.target.checked }))}
            />
            {t('documents.billing.released')}
          </label>
          {v.releasedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('documents.billing.releasedBy', { date: new Date(v.releasedAt).toLocaleDateString(navigator.language), name: v.releasedByName ?? '—' })}
            </p>
          )}
        </div>

        <div>
          <p className={label}>{t('documents.billing.signedPdf')}</p>
          {v.signedPdfName ? (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <button type="button" onClick={() => void laden()} className="inline-flex items-center gap-1 text-primary hover:underline">
                <Paperclip className="h-3.5 w-3.5" />
                {v.signedPdfName}
              </button>
              {darf && (
                <button type="button" disabled={laeuft} onClick={() => void aktion(() => documentsApi.deleteSignedPdf(beleg.id))} className="text-xs text-muted-foreground hover:text-destructive">
                  {t('documents.billing.removePdf')}
                </button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          {darf && (
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input px-2 py-1 text-xs hover:bg-muted">
              <Upload className="h-3.5 w-3.5" />
              {t(v.signedPdfName ? 'documents.billing.replacePdf' : 'documents.billing.uploadPdf')}
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const datei = e.target.files?.[0]
                  e.target.value = ''
                  if (datei) void aktion(() => documentsApi.uploadSignedPdf(beleg.id, datei))
                }}
              />
            </label>
          )}
        </div>
      </div>
    </section>
  )
}
